import { ArrowSquareOutIcon, QuestionIcon, TrashSimpleIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { createFormHook, createFormHookContexts, useStore } from '@tanstack/react-form'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import * as footerStyles from '../../../components/drawers/floating-footer.css.ts'
import { FloatingFooter } from '../../../components/drawers/floating-footer.tsx'
import { RadioGroup, RadioItem } from '../../../components/input/radio.tsx'
import { SelectBox } from '../../../components/input/select-box.tsx'
import { TextInput } from '../../../components/input/text-input.tsx'
import { TooltipWithHover } from '../../../components/tooltip.tsx'
import { useShowMutationError } from '../../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import { usePutIntegration, type PutIntegrationVariables } from '../../../utils/api-client/api-hooks.ts'
import type { Integration } from '../../../utils/api-client/api-schemas.ts'
import { getIsIntegrationTypeSupportedByApi } from '../../../utils/api-client/api-schemas.ts'
import { DeleteIntegrationModal } from './delete-integration-modal.tsx'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import { HealthDot } from './instance-card.tsx'
import * as styles from './integration-drawer.css.ts'
import { IntegrationBrandIcon } from './integration-icon.tsx'
import type { CreatableIntegrationType, IntegrationField } from './integration-instance-utils.ts'
import {
  TOOL_FIELD_CONFIG,
  getCatalogEntryForType,
  getHasIntegrationSetupForm,
  getInstanceFailureDetail,
  getInstanceHealth,
  getInstanceName,
  getProviderLabel,
} from './integration-instance-utils.ts'

// #region Types

type WebhookAuthMethod = 'unchanged' | 'secret' | 'basic'

// Field state lives in the TanStack Form store (not in a component that renders Dialog.Content) so
// typing never re-renders Dialog.Content — that would re-run Radix's FocusScope ref callback and
// exceed React's update-depth limit.
/** Exactly the three keys the webhook fieldset renders, so a typo is a build error, not an empty read. */
type WebhookValues = { webhook_secret: string; webhook_user: string; webhook_password: string }

type ManageFormValues = {
  name: string
  values: Record<string, string>
  webhookMethod: WebhookAuthMethod
  webhookValues: WebhookValues
}

type ManageDrawerProps = {
  integration: Integration
  onClose: () => unknown
}

// #endregion

// #region Form hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: ManageFormValues = {
  name: '',
  values: {},
  webhookMethod: 'unchanged',
  webhookValues: { webhook_secret: '', webhook_user: '', webhook_password: '' },
}

const useManageForm = (defaultValueOverrides: Partial<ManageFormValues> = {}) =>
  useAppForm({ defaultValues: { ...FORM_DEFAULT_VALUES, ...defaultValueOverrides } })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

// Reads a non-secret field's stored value off an integration response so it can prefill the edit
// field. Credentials are write-only and never returned, so this only ever surfaces values like the
// base URI; fields the response omits (e.g. Checkmarx region/tenant) start empty.
const getStoredValue = (integration: Integration, key: string): string => {
  const stored: unknown = Reflect.get(integration, key)
  return typeof stored === 'string' ? stored : ''
}

// Trims and includes a changed-or-newly-entered field; only non-empty values are sent, because the
// platform rejects blanks and credentials are write-only "rotate on re-entry".
const editedOrUndefined = (value: string, original: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed !== '' && trimmed !== original ? trimmed : undefined
}

// A pasted credential routinely carries trailing whitespace, which fails auth with nothing to point at.
const secretOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed !== '' ? trimmed : undefined
}

/**
 * How far through re-entering a tool's credentials the user is. The platform takes a tool's secrets
 * as a unit — supply all of them to rotate, omit all of them to keep what is stored — so a partly
 * filled set is neither. Saving one would quietly drop it and report success, so `partial` blocks the
 * save and says why instead.
 */
type SecretRotation = 'none' | 'partial' | 'complete'

const getSecretRotation = (fields: IntegrationField[], values: Record<string, string>): SecretRotation => {
  const secretFields = fields.filter(field => field.secret)
  // Trimmed, so a field holding only whitespace counts as untouched rather than as a rotation.
  const enteredCount = secretFields.filter(field => (values[field.key] ?? '').trim() !== '').length
  if (enteredCount === 0) {
    return 'none'
  }
  return enteredCount === secretFields.length ? 'complete' : 'partial'
}

const PARTIAL_SECRET_MESSAGE = 'Re-enter every credential for this tool to rotate them, or leave them all blank.'

/**
 * The webhook credentials are a set for the same reason a tool's are: basic auth is a username and a
 * password together, and the platform takes both or neither. They sit outside `TOOL_FIELD_CONFIG`
 * — the drawer renders them through a method chooser — so `getSecretRotation` never sees them.
 */
const getWebhookRotation = (method: WebhookAuthMethod, values: WebhookValues): SecretRotation =>
  match(method)
    .with('unchanged', () => 'none' as const)
    .with('secret', () => (values.webhook_secret.trim() !== '' ? ('complete' as const) : ('none' as const)))
    .with('basic', () => {
      const entered = [values.webhook_user, values.webhook_password].filter(value => value.trim() !== '').length
      if (entered === 0) {
        return 'none' as const
      }
      return entered === 2 ? ('complete' as const) : ('partial' as const)
    })
    .exhaustive()

type ManageContext = {
  tool: CreatableIntegrationType | undefined
  fields: IntegrationField[]
  originalName: string
  originalValues: Record<string, string>
}

// Recomputed by both the field and footer leaves so neither has to thread it through the form store.
const deriveManageContext = (integration: Integration): ManageContext => {
  const tool = getHasIntegrationSetupForm(integration.type) ? integration.type : undefined
  const fields = tool ? TOOL_FIELD_CONFIG[tool] : []
  return {
    tool,
    fields,
    originalName: getInstanceName(integration),
    originalValues: Object.fromEntries(
      fields.map(field => [field.key, field.secret ? '' : getStoredValue(integration, field.key)])
    ),
  }
}

// The fields whose value changed from the stored instance, ready to spread into the request body.
const collectChangedFields = (
  fields: IntegrationField[],
  values: Record<string, string>,
  originalValues: Record<string, string>
): Array<readonly [string, string]> =>
  fields
    .map(field => {
      const value = values[field.key] ?? ''
      const sent = field.secret ? secretOrUndefined(value) : editedOrUndefined(value, originalValues[field.key] ?? '')
      return sent === undefined ? undefined : ([field.key, sent] as const)
    })
    .filter((entry): entry is readonly [string, string] => entry !== undefined)

// #endregion

// Secrets are write-only: the key fields render empty and are only sent when re-entered to rotate.
export const ManageDrawer: React.FC<ManageDrawerProps> = ({ integration, onClose }) => {
  const health = getInstanceHealth(integration)
  invariant(integration.id, 'A configured integration always has an id')
  const { originalName, originalValues } = deriveManageContext(integration)
  const form = useManageForm({ name: originalName, values: originalValues })
  const drawerContentRef = useRef<HTMLDivElement>(null)

  const toolLabel = getProviderLabel(integration.type)
  const toolUrl = 'base_uri' in integration && integration.base_uri ? integration.base_uri : undefined

  return (
    <Dialog.Root open modal={false} onOpenChange={open => (!open ? onClose() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <form.AppForm>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onInteractOutside={event => event.preventDefault()}
          >
            <div className={styles.header}>
              <div className={styles.titleRow}>
                <IntegrationBrandIcon integration={integration} />
                <div className={styles.titleText}>
                  <div className={styles.titleNameRow}>
                    <Dialog.Title className={styles.title}>{toolLabel}</Dialog.Title>
                    <HealthDot health={health} failureDetail={getInstanceFailureDetail(integration)} />
                  </div>
                  {toolUrl && (
                    <a className={styles.viewInToolLink} href={toolUrl} target="_blank" rel="noreferrer">
                      View in {toolLabel}
                      <ArrowSquareOutIcon size={16} weight="regular" className={styles.viewInToolIcon} />
                    </a>
                  )}
                </div>
              </div>
              <Dialog.Close asChild>
                <button type="button" className={styles.closeButton} aria-label="Close">
                  <XIcon size={24} weight="regular" />
                </button>
              </Dialog.Close>
            </div>

            <ManageFields integration={integration} />

            <VisuallyHidden>
              <Dialog.Description>Manage the {getInstanceName(integration)} integration.</Dialog.Description>
            </VisuallyHidden>
          </Dialog.Content>

          <FloatingFooter drawerContentRef={drawerContentRef} width="standard">
            <ManageFooter integration={integration} onClose={onClose} />
          </FloatingFooter>
        </form.AppForm>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #region Field leaves

const ManageFields: React.FC<{ integration: Integration }> = ({ integration }) => {
  const form = useFormContext()
  const { tool, fields } = deriveManageContext(integration)
  const name = useStore(form.store, state => state.values.name)
  const values = useStore(form.store, state => state.values.values)
  const isSecretRotationPartial = getSecretRotation(fields, values) === 'partial'

  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionHeading}>Manage your instance</h3>
      <div className={styles.fields}>
        <TextInput
          id="manage-name"
          label="Name"
          explicitLabelCondition="optional"
          value={name}
          onChange={value => form.setFieldValue('name', value)}
          placeholder="Name this integration"
        />
        {fields.map(field =>
          match(field)
            .with({ kind: 'select' }, selectField => (
              <SelectBox
                key={selectField.key}
                id={`manage-${selectField.key}`}
                label={selectField.label}
                value={values[selectField.key]}
                placeholder="Select to change"
                onValueChange={value => setValue(selectField.key, value)}
                options={selectField.options ?? []}
              />
            ))
            .otherwise(textField => (
              <TextInput
                key={textField.key}
                id={`manage-${textField.key}`}
                label={textField.label}
                type={textField.kind === 'password' ? 'password' : 'text'}
                value={values[textField.key]}
                onChange={value => setValue(textField.key, value)}
                placeholder={textField.secret ? 'Re-enter to rotate' : undefined}
                errorText={
                  textField.secret && isSecretRotationPartial && (values[textField.key] ?? '') === ''
                    ? PARTIAL_SECRET_MESSAGE
                    : undefined
                }
              />
            ))
        )}
        {tool === 'appscan' && <ManageWebhookAuthFieldset />}
      </div>
    </div>
  )
}

// The AppScan webhook-auth rotation chooser. "Leave unchanged" keeps the stored credentials; the
// other two options each rotate exactly one method (secret OR the basic-auth pair), mirroring the
// platform's exclusive-OR rule.
const ManageWebhookAuthFieldset: React.FC = () => {
  const form = useFormContext()
  const method = useStore(form.store, state => state.values.webhookMethod)
  const webhookValues = useStore(form.store, state => state.values.webhookValues)
  const isBasicPairPartial = getWebhookRotation(method, webhookValues) === 'partial'
  const setWebhookValue = (key: string, value: string) =>
    form.setFieldValue('webhookValues', previous => ({ ...previous, [key]: value }))

  return (
    <fieldset className={styles.webhookAuthFieldset}>
      <legend className={styles.sectionHeading}>Webhook authentication</legend>
      <RadioGroup
        className={styles.webhookAuthOptions}
        value={method}
        onValueChange={value =>
          form.setFieldValue(
            'webhookMethod',
            match(value)
              .with('basic', () => 'basic' as const)
              .with('secret', () => 'secret' as const)
              .otherwise(() => 'unchanged' as const)
          )
        }
      >
        <RadioItem id="manage-webhook-method-unchanged" value="unchanged" label="Leave unchanged" />
        <RadioItem id="manage-webhook-method-secret" value="secret" label="Shared secret" />
        <RadioItem id="manage-webhook-method-basic" value="basic" label="Basic auth" />
      </RadioGroup>
      {match(method)
        .with('secret', () => (
          <TextInput
            id="manage-webhook_secret"
            label="Webhook secret"
            type="password"
            value={webhookValues.webhook_secret}
            onChange={value => setWebhookValue('webhook_secret', value)}
            placeholder="Re-enter to rotate"
          />
        ))
        .with('basic', () => (
          <>
            <TextInput
              id="manage-webhook_user"
              label="Webhook username"
              value={webhookValues.webhook_user}
              onChange={value => setWebhookValue('webhook_user', value)}
              errorText={
                isBasicPairPartial && webhookValues.webhook_user.trim() === '' ? PARTIAL_SECRET_MESSAGE : undefined
              }
            />
            <TextInput
              id="manage-webhook_password"
              label="Webhook password"
              type="password"
              value={webhookValues.webhook_password}
              onChange={value => setWebhookValue('webhook_password', value)}
              placeholder="Re-enter to rotate"
              errorText={
                isBasicPairPartial && webhookValues.webhook_password.trim() === '' ? PARTIAL_SECRET_MESSAGE : undefined
              }
            />
          </>
        ))
        .with('unchanged', () => undefined)
        .exhaustive()}
    </fieldset>
  )
}

// #endregion

// #region Footer leaf

type ManageFooterProps = { integration: Integration; onClose: () => unknown }

const ManageFooter: React.FC<ManageFooterProps> = ({ integration, onClose }) => {
  const form = useFormContext()
  const { tool, fields, originalName, originalValues } = deriveManageContext(integration)
  const values = useStore(form.store, state => state.values.values)
  const name = useStore(form.store, state => state.values.name)
  const webhookMethod = useStore(form.store, state => state.values.webhookMethod)
  const webhookValues = useStore(form.store, state => state.values.webhookValues)

  const { addToast } = useToastService()
  const [isDocsOpen, setIsDocsOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const docsEntry = getCatalogEntryForType(integration.type)

  const showSaveError = useShowMutationError({
    prefix: 'Failed to save integration',
    overrides: { 409: { message: 'An integration with this name already exists.' } },
  })
  const putIntegration = usePutIntegration({
    onSuccess: () => {
      addToast({ message: <>{getInstanceName(integration)} updated.</>, variant: 'success' })
      onClose()
    },
    onError: showSaveError,
  })

  const isBusy = putIntegration.isPending

  const trimmedName = name.trim()
  const isNameChanged = trimmedName !== '' && trimmedName !== originalName
  const changedFields = collectChangedFields(fields, values, originalValues)
  const secretRotation = getSecretRotation(fields, values)
  const webhookRotation = getWebhookRotation(webhookMethod, webhookValues)
  const hasChanges = isNameChanged || changedFields.length > 0 || webhookRotation !== 'none'
  const isSavable = hasChanges && secretRotation !== 'partial' && webhookRotation !== 'partial'

  const handleSave = () => {
    // Only a tool with a field config can build a body. `toConfiguredInstances` filters on `source`,
    // not type, so an SCM integration marked `managed` would arrive here with no config — and the
    // drawer would look editable while Save did nothing. Fail loudly instead of silently.
    invariant(tool !== undefined, `ManageDrawer opened for ${integration.type}, which has no editable fields`)
    const webhook = match(webhookMethod)
      .with('secret', () => ({ webhook_secret: secretOrUndefined(webhookValues.webhook_secret) }))
      .with('basic', () => ({
        webhook_user: secretOrUndefined(webhookValues.webhook_user),
        webhook_password: secretOrUndefined(webhookValues.webhook_password),
      }))
      .with('unchanged', () => ({}))
      .exhaustive()
    const changed = Object.fromEntries(changedFields)
    // PUT merges rather than replaces: a field carrying a value replaces it, an omitted field keeps
    // what is stored. So omitting the base URI is safe, and it is sent only when known — never blank,
    // which the endpoint rejects. Credentials go as a set or not at all: `complete` rotates them,
    // anything else leaves the stored ones alone. Only `datadog` is a permitted update type today,
    // so the ahead-of-spec arm below is shaped for when its contract lands, not for what ships now.
    const baseUri = changed.base_uri ?? originalValues.base_uri
    const body: PutIntegrationVariables['body'] = getIsIntegrationTypeSupportedByApi(tool)
      ? {
          type: tool,
          name: isNameChanged ? trimmedName : originalName,
          base_uri: baseUri !== '' ? baseUri : undefined,
          api_key: secretRotation === 'complete' ? changed.api_key : undefined,
          application_key: secretRotation === 'complete' ? changed.application_key : undefined,
        }
      : { type: tool, name: isNameChanged ? trimmedName : originalName, ...changed, ...webhook }
    invariant(integration.id, 'A configured integration always has an id')
    putIntegration.mutate({ integrationId: integration.id, body })
  }

  return (
    <>
      {/* Saving and deleting are mutually exclusive on the same instance, so a save in flight rules
          delete out until it settles. */}
      <GhostButton
        type="destructive"
        state={isBusy ? 'disabled' : 'default'}
        onClick={() => setIsDeleteModalOpen(true)}
      >
        <span className={styles.iconLabel}>
          <TrashSimpleIcon size={16} weight="regular" />
          Delete integration
        </span>
      </GhostButton>
      <div className={styles.footerRightGroup}>
        {docsEntry && (
          <TooltipWithHover
            trigger={
              <button
                type="button"
                className={styles.docsIconButton}
                onClick={() => setIsDocsOpen(prev => !prev)}
                aria-label="View documentation"
              >
                <QuestionIcon size={20} weight="regular" />
              </button>
            }
          >
            View documentation
          </TooltipWithHover>
        )}
        <GhostButton type="secondary" state={isBusy ? 'disabled' : 'default'} onClick={onClose}>
          Cancel
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={isBusy ? 'loading' : isSavable ? 'default' : 'disabled'}
          onClick={handleSave}
          loadingLabel="Saving"
        >
          Test and save changes
        </DefaultButton>
      </div>
      {isDocsOpen && docsEntry && (
        <DocumentationDrawer entry={docsEntry} placement="beside" onClose={() => setIsDocsOpen(false)} />
      )}
      {isDeleteModalOpen && (
        <DeleteIntegrationModal
          integration={integration}
          onCancel={() => setIsDeleteModalOpen(false)}
          onSuccess={onClose}
        />
      )}
    </>
  )
}

// #endregion
